import { useState, useEffect, useCallback, useRef } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { Download, FileText, FileSpreadsheet, Printer, Calendar, TrendingUp, Users, BarChart3, Loader2, RefreshCw } from 'lucide-react';

const PERIODS = [
  { k: '7d', l: 'Últimos 7 días' },
  { k: '30d', l: 'Últimos 30 días' },
  { k: 'month', l: 'Este mes' },
  { k: 'custom', l: 'Personalizado' },
];

export default function Reports() {
  const [period, setPeriod] = useState('30d');
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [reservations, setReservations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const getDateRange = () => {
    const today = new Date();
    if (period === '7d') return { from: format(subDays(today, 7), 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
    if (period === '30d') return { from: format(subDays(today, 30), 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
    if (period === 'month') return { from: format(startOfMonth(today), 'yyyy-MM-dd'), to: format(endOfMonth(today), 'yyyy-MM-dd') };
    return { from: dateFrom, to: dateTo };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const [resR, custR] = await Promise.all([
        api.get(`/reservations?date_from=${from}&date_to=${to}`),
        api.get('/customers'),
      ]);
      setReservations(resR.data || []);
      setCustomers(custR.data || []);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, [period, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Stats
  const total = reservations.length;
  const confirmed = reservations.filter(r => ['confirmed', 'seated', 'completed'].includes(r.status)).length;
  const cancelled = reservations.filter(r => r.status === 'cancelled').length;
  const noShow = reservations.filter(r => r.status === 'no_show').length;
  const totalGuests = reservations.reduce((s, r) => s + (r.party_size || 0), 0);
  const avgParty = total > 0 ? (totalGuests / total).toFixed(1) : 0;
  const conversionRate = total > 0 ? Math.round((confirmed / total) * 100) : 0;

  // Breakdown by status
  const byStatus = {
    confirmed: confirmed,
    cancelled: cancelled,
    no_show: noShow,
    pending: reservations.filter(r => r.status === 'pending').length,
  };

  // Top times
  const timeMap = {};
  reservations.forEach(r => {
    if (r.time) { const h = r.time.split(':')[0]; timeMap[h] = (timeMap[h] || 0) + 1; }
  });
  const topTimes = Object.entries(timeMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const exportCSV = () => {
    const headers = ['Fecha', 'Nombre', 'Teléfono', 'Personas', 'Hora', 'Mesa', 'Estado', 'Notas'];
    const rows = reservations.map(r => [
      r.date || '', r.guest_name || '', r.guest_phone || '',
      r.party_size || '', r.time || '', r.table_name || '', r.status || '', r.notes || ''
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `reservas-${getDateRange().from}-${getDateRange().to}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV descargado');
  };

  const printReport = () => window.print();

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Informes" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  const { from, to } = getDateRange();

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Informes" actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} className="h-8 text-xs">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-green-600" /> Exportar CSV
          </Button>
          <Button variant="outline" size="sm" onClick={printReport} className="h-8 text-xs">
            <Printer className="h-3.5 w-3.5 mr-1" /> Imprimir / PDF
          </Button>
        </div>
      } />

      <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-6" ref={printRef}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Informe de Reservas</h1>
            <p className="text-sm text-slate-500">{format(new Date(from + 'T12:00'), "d MMM yyyy", { locale: es })} – {format(new Date(to + 'T12:00'), "d MMM yyyy", { locale: es })}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={p => { setPeriod(p); }}>
              <SelectTrigger className="w-44 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(p => <SelectItem key={p.k} value={p.k}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
            {period === 'custom' && (
              <>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-sm" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-sm" />
              </>
            )}
            <Button size="sm" onClick={fetchData} variant="outline" className="h-9"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: 'Total reservas', v: total, icon: Calendar, c: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Confirmadas', v: confirmed, icon: TrendingUp, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Total comensales', v: totalGuests, icon: Users, c: 'text-purple-600', bg: 'bg-purple-50' },
            { l: 'Tasa de conversión', v: `${conversionRate}%`, icon: BarChart3, c: 'text-orange-600', bg: 'bg-orange-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-5 w-5", s.c)} />
                </div>
                <div className="text-3xl font-black text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Status breakdown */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-slate-700">Por estado</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[
                { l: 'Confirmadas', v: byStatus.confirmed, c: 'bg-green-500' },
                { l: 'Pendientes', v: byStatus.pending, c: 'bg-amber-400' },
                { l: 'Canceladas', v: byStatus.cancelled, c: 'bg-red-400' },
                { l: 'No-show', v: byStatus.no_show, c: 'bg-slate-400' },
              ].map(s => (
                <div key={s.l}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{s.l}</span>
                    <span className="font-semibold text-slate-800">{s.v} ({total > 0 ? Math.round(s.v / total * 100) : 0}%)</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", s.c)} style={{ width: `${total > 0 ? (s.v / total * 100) : 0}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top hours */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-slate-700">Horas más populares</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {topTimes.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">Sin datos</p>
              ) : topTimes.map(([h, count]) => (
                <div key={h}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{h}:00 – {h}:59</span>
                    <span className="font-semibold text-slate-800">{count} reservas</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-orange-400 transition-all"
                      style={{ width: `${total > 0 ? (count / total * 100) : 0}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Data Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">Detalle de reservas ({total})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  {['Fecha', 'Cliente', 'Teléfono', 'Personas', 'Hora', 'Estado'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {reservations.slice(0, 50).map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs text-slate-600">{r.date || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{r.guest_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.guest_phone || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{r.party_size || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.time || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold",
                        r.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        r.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        r.status === 'no_show' ? 'bg-slate-100 text-slate-600' :
                        r.status === 'seated' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700')}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {total === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">Sin reservas en el período seleccionado</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
